import { lazy } from "react";

/**
 * Overlay Entity Registry
 *
 * Single source of truth for overlay configuration.
 * Width is controlled ONLY here - detail components must not set width.
 *
 * allowOverlay: true  → opens in overlay (left click) or full page (ctrl+click)
 * allowOverlay: false → always navigates to full page, never opens overlay
 */
export const OVERLAY_ENTITIES = {
  item: {
    component: lazy(
      () => import("../views/app_pages/Product/components/DetailComponent"),
    ),
    defaultReturnTo: "/items",
    width: 800,
    allowOverlay: true,
  },
  "item-group": {
    component: lazy(
      () =>
        import("../views/app_pages/ProductGroup/components/DetailComponent"),
    ),
    defaultReturnTo: "/item-groups",
    width: 600,
    allowOverlay: true,
  },
  "composite-item": {
    component: lazy(
      () =>
        import("../views/app_pages/CompositeItems/components/DetailComponent"),
    ),
    defaultReturnTo: "/composite-items",
    width: 800,
    allowOverlay: true,
  },
  vendor: {
    component: lazy(
      () => import("../views/app_pages/Vendor/components/DetailComponent"),
    ),
    defaultReturnTo: "/vendors",
    width: 600,
    allowOverlay: true,
  },
  customer: {
    component: lazy(
      () => import("../views/app_pages/Customer/components/DetailComponent"),
    ),
    defaultReturnTo: "/customers",
    width: 600,
    allowOverlay: true,
  },
  "purchase-order": {
    component: lazy(
      () =>
        import("../views/app_pages/Procurement/PurchaseOrder/components/DetailComponent"),
    ),
    defaultReturnTo: "/purchase-orders",
    width: 900,
    allowOverlay: true,
  },
  "purchase-receipt": {
    component: lazy(
      () =>
        import("../views/app_pages/Procurement/PurchaseReceive/components/DetailComponent"),
    ),
    defaultReturnTo: "/purchase-receipts",
    width: 900,
    allowOverlay: true,
  },
  // Invoice-wise Purchase Receive detail (the panel the Invoice-wise tab opens
  // on row click). Keyed by invoiceNumber → /purchase-receipts/invoice/:invoiceNumber.
  "purchase-receive-invoice": {
    component: lazy(
      () =>
        import("../views/app_pages/Procurement/PurchaseReceive/components/InvoiceDetailComponent"),
    ),
    defaultReturnTo: "/purchase-receipts/invoice",
    width: 900,
    allowOverlay: true,
  },
  "sales-order": {
    component: lazy(
      () =>
        import("../views/app_pages/Sales/SalesOrder/components/DetailComponent"),
    ),
    defaultReturnTo: "/sales-orders",
    width: 900,
    allowOverlay: true,
  },
  invoice: {
    component: lazy(
      () =>
        import("../views/app_pages/Sales/Invoice/components/DetailComponent"),
    ),
    defaultReturnTo: "/invoices",
    width: 900,
    allowOverlay: true,
  },
  quotation: {
    component: lazy(
      () =>
        import("../views/app_pages/Sales/Quotation/components/DetailComponent"),
    ),
    defaultReturnTo: "/quotations",
    width: 900,
    allowOverlay: true,
  },
  // Counter Sale — opened from the Sales By Item overlay's new Counter Sales
  // tab. Reuses the same DetailComponent the CS list panel renders, so the
  // overlay shows the full CS detail (Overview / Items / E-Invoice tabs +
  // Edit / Print / Download / Cancel actions, each permission-gated inside
  // the DetailComponent itself). Users without CounterSales.View see the
  // RestrictedAccessPage from the DetailComponent's own check.
  "counter-sale": {
    component: lazy(
      () =>
        import(
          "../views/app_pages/Sales/CounterSale/components/DetailComponent"
        ),
    ),
    defaultReturnTo: "/counter-sales",
    width: 900,
    allowOverlay: true,
  },
  // Note: First package entry removed (duplicate). See consolidated entry below.
  // package: {
  //   component: lazy(
  //     () => import("../views/app_pages/Package/components/DetailComponent"),
  //   ),
  //   defaultReturnTo: "/packages",
  //   width: 900,
  //   allowOverlay: false, // Always navigate to full page
  // },
  picklist: {
    component: lazy(
      () =>
        import("../views/app_pages/Warehouse/Picklist/components/DetailComponent"),
    ),
    defaultReturnTo: "/picklists",
    width: 900,
    allowOverlay: true,
  },
  package: {
    component: lazy(
      () => import("../views/app_pages/Package/components/DetailComponent"),
    ),
    defaultReturnTo: "/packages",
    width: 900,
    allowOverlay: true,
  },
  "sales-return": {
    component: lazy(
      () =>
        import("../views/app_pages/Sales/SalesReturn/components/DetailComponent"),
    ),
    defaultReturnTo: "/sales-returns",
    width: 900,
    allowOverlay: true,
  },
  backorder: {
    component: lazy(
      () =>
        import("../views/app_pages/Warehouse/Backorder/components/DetailComponent"),
    ),
    defaultReturnTo: "/backorders",
    width: 800,
    allowOverlay: true,
  },
  shipment: {
    component: lazy(
      () => import("../views/app_pages/Shipment/components/DetailComponent"),
    ),
    defaultReturnTo: "/shipment",
    width: 900,
    allowOverlay: true,
  },
  "credit-note": {
    component: lazy(
      () =>
        import("../views/app_pages/Sales/CreditNote/components/DetailComponent"),
    ),
    defaultReturnTo: "/credit-notes",
    width: 900,
    allowOverlay: true,
  },
  "delivery-challan": {
    component: lazy(
      () =>
        import("../views/app_pages/DeliveryChallan/components/DetailComponent"),
    ),
    defaultReturnTo: "/delivery-challans",
    width: 900,
    allowOverlay: true,
  },
  role: {
    component: lazy(
      () => import("../views/app_pages/Role/components/DetailComponent"),
    ),
    defaultReturnTo: "/roles",
    width: 900,
    allowOverlay: true,
  },
};

/**
 * Get overlay configuration for an entity
 * @param {string} entity - Entity type key
 * @returns {Object|null} Entity overlay configuration or null if not found
 */
export const getOverlayConfig = (entity) => {
  return OVERLAY_ENTITIES[entity] || null;
};

/**
 * Get overlay width for an entity
 * @param {string} entity - Entity type key
 * @returns {number} Width in pixels (defaults to 800)
 */
export const getOverlayWidth = (entity) => {
  const config = OVERLAY_ENTITIES[entity];
  return config?.width || 800;
};

/**
 * Get default return path for an entity
 * @param {string} entity - Entity type key
 * @returns {string} Default return path
 */
export const getDefaultReturnTo = (entity) => {
  const config = OVERLAY_ENTITIES[entity];
  return config?.defaultReturnTo || "/";
};

/**
 * Check if overlay is allowed for an entity
 * @param {string} entity - Entity type key
 * @returns {boolean} True if overlay is allowed, false if should navigate to full page
 */
export const isOverlayAllowed = (entity) => {
  const config = OVERLAY_ENTITIES[entity];
  // Default to true if entity not found or allowOverlay not specified
  return config?.allowOverlay !== false;
};

/**
 * Get full page route for an entity
 * @param {string} entity - Entity type key
 * @param {string|number} id - Entity ID
 * @returns {string} Full page route path
 */
export const getEntityRoute = (entity, id) => {
  const config = OVERLAY_ENTITIES[entity];
  // Use custom route pattern if specified
  if (config?.routePattern) {
    return config.routePattern.replace(":id", id);
  }
  if (!config?.defaultReturnTo) {
    return `/${entity}s/${id}`;
  }
  return `${config.defaultReturnTo}/${id}`;
};

export default OVERLAY_ENTITIES;
